import { Directive, OnDestroy, OnInit, ViewContainerRef, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { findLast, isNil } from 'lodash';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ColDef, ColumnState, Column } from 'ag-grid-community';
import { AgGridAngular } from 'ag-grid-angular';

@Directive({
  selector: 'ag-grid-angular[extendLastColumn]',
  standalone: false,
})
export class AgGridExtendLastColumnDirective implements OnInit, OnDestroy {
  private readonly agGrid = inject(AgGridAngular);
  private readonly agGridElementRef = inject(ViewContainerRef);

  readonly processListOrder = 6;

  agGridElement: HTMLElement;

  private readonly unsubscribe$ = new Subject<void>();

  ngOnInit(): void {
    this.agGridElement = this.agGridElementRef.element.nativeElement as HTMLElement;
    this.handleColumnResize();
  }

  private handleColumnResize(): void {
    this.agGrid.rowDataUpdated.pipe(
      switchMap(() => this.agGrid.columnResized),
      filter(event => event.type === 'columnResized' && event.finished),
      tap(() => this.checkAndUpdateForLastNonPinnedColumn()),
      takeUntil(this.unsubscribe$),
    ).subscribe();
  }

  /**
   * Finds the last non pinned column. Sets width and maxWidth to undefined to make it available to apply flex property.
   * Sets flex to 1 so the column can be stretched to fill the remaining space.
   * @param colDefList
   * @protected
   */
  protected setLastNonPinnedColumnFlexible(colDefList: ColDef[]): void {
    const lastNonPinnedColDef = this.getLastNonPinnedColDef(colDefList);

    if (lastNonPinnedColDef) {
      lastNonPinnedColDef.width = undefined;
      lastNonPinnedColDef.maxWidth = undefined;
      lastNonPinnedColDef.flex = 1;
    }
  }

  private getLastNonPinnedColDef(colDefList: ColDef[]): ColDef {
    return findLast(colDefList, colDef => {
      return isNil(colDef.pinned);
    });
  }

  private checkAndUpdateForLastNonPinnedColumn(): void {
    const lastColumn = this.agGrid.api?.getColumn(this.getLastNonPinnedColDef(this.agGrid.api?.getColumnDefs())?.colId);
    if (lastColumn && !this.isNonPinnedContentWideEnough()) {
      this.resetWidthOfColumn(lastColumn);
    }
  }

  protected isNonPinnedContentWideEnough(): boolean {
    const centerViewPortWidth = this.agGridElement.getElementsByClassName('ag-center-cols-viewport')?.[0]?.clientWidth;
    if (isNil(centerViewPortWidth)) {
      return true;
    }

    let allNonPinnedColumnsWidth = 0;
    this.agGrid.api?.getColumns().forEach(col => {
      if (isNil(col.getPinned())) {
        allNonPinnedColumnsWidth += col.getActualWidth();
      }
    });
    return centerViewPortWidth < allNonPinnedColumnsWidth;
  }

  protected resetWidthOfColumn(column: Column): void {
    const columnState = this.agGrid.api?.getColumnState();
    const stateOfCurrentColumnIndex = columnState.findIndex(colState => colState.colId === column.getColId());

    if (stateOfCurrentColumnIndex >= 0) {
      const newState: ColumnState = {
        ...columnState[stateOfCurrentColumnIndex],
        flex: 1,
      };

      const newColumnState = [...columnState];
      newColumnState.splice(stateOfCurrentColumnIndex, 1, newState);
      this.agGrid.api?.applyColumnState({
        state: newColumnState,
      });
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
